import React, {useEffect, useRef, useState} from "react";
import s from "./BannerPromotion.module.scss";
import api from "../../../api/client";

const BannerPromotion = () => {

    const scrollerRef = useRef(null);
    const [promotions, setPromotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeDot, setActiveDot] = useState(0);

    useEffect(() => {

        const fetchPromotions = async () => {

            try {

                const response = await api.get(
                    "/api/banner-promotions"
                );

                const data = Array.isArray(response.data)
                    ? response.data
                    : [response.data];

                setPromotions(
                    data
                        .filter(
                            promotion =>
                                promotion.status === true &&
                                (promotion.image_url || promotion.image_url_mobile) &&
                                (promotion.tour || promotion.link)
                        )
                        .sort(
                            (a, b) =>
                                (a.order ?? 0) - (b.order ?? 0)
                        )
                );

            } catch (error) {

                console.error(
                    "Failed to load banner promotions",
                    error
                );

                setPromotions([]);

            } finally {

                setLoading(false);

            }

        };

        fetchPromotions();

    }, []);

    if (loading || !promotions.length) {
        return null;
    }


    const normalizeInternalLink = (link) => {
        if (!link) {
            return "";
        }

        if (link.startsWith("/")) {
            return link;
        }

        try {
            const url = new URL(link);

            if (url.origin === window.location.origin) {
                return `${url.pathname}${url.search}${url.hash}`;
            }

            return link;
        } catch (error) {
            return `/${link}`;
        }
    };

    const getBannerHref = (promotion) => {
        const tourId =
            promotion.tour?.tour_id ||
            promotion.tour?.rezdy_id ||
            promotion.tour_id ||
            promotion.tour?.id;

        if (promotion.tour && tourId) {
            return `/tours/${tourId}`;
        }

        return normalizeInternalLink(promotion.link);
    };

    const isExternalLink = (href) => {
        return /^https?:\/\//i.test(href) &&
            !href.startsWith(window.location.origin);
    };

    const scrollToDot = (index) => {
        const el = scrollerRef.current;

        if (!el) return;

        const target = el.children[index];

        el.scrollTo({
            left: target
                ? target.offsetLeft - el.offsetLeft
                : el.clientWidth * index,
            behavior: "smooth"
        });
    };

    const scrollPrev = () => {
        scrollToDot(
            Math.max(activeDot - 1, 0)
        );
    };

    const scrollNext = () => {
        scrollToDot(
            Math.min(activeDot + 1, promotions.length - 1)
        );
    };

    const handleScroll = () => {
        const el = scrollerRef.current;

        if (!el) return;

        const index = Math.round(
            el.scrollLeft / el.clientWidth
        );

        setActiveDot(
            Math.min(index, promotions.length - 1)
        );
    };


    return (

        <section className={s.bannerPromotionSection}>

            <div className={s.container}>

                <div className={s.scrollerWrap}>
                    <div
                        ref={scrollerRef}
                        className={s.bannerList}
                        onScroll={handleScroll}
                    >

                        {promotions.map(promotion => {
                            const href = getBannerHref(promotion);
                            const external = isExternalLink(href);
                            const desktopImage = promotion.image_url || promotion.image_url_mobile;
                            const mobileImage = promotion.image_url_mobile || promotion.image_url;

                            return (
                                <a
                                    key={promotion.id}
                                    className={s.banner}
                                    href={href}
                                    target={
                                        external
                                            ? "_blank"
                                            : undefined
                                    }
                                    rel={
                                        external
                                            ? "noopener noreferrer"
                                            : undefined
                                    }
                                    aria-label={
                                        promotion.tour?.title ||
                                        "Promotion"
                                    }
                                >

                                    <picture>
                                        <source
                                            media="(max-width: 768px)"
                                            srcSet={mobileImage}
                                        />
                                        <img
                                            src={desktopImage}
                                            alt={
                                                promotion.tour?.title ||
                                                "Promotion"
                                            }
                                        />
                                    </picture>

                                </a>
                            );
                        })}

                    </div>

                    {promotions.length > 1 && (
                        <>
                            <button
                                type="button"
                                className={`${s.navButton} ${s.prevButton}`}
                                onClick={scrollPrev}
                                disabled={activeDot === 0}
                                aria-label="Previous banner"
                            >
                                &#8249;
                            </button>

                            <button
                                type="button"
                                className={`${s.navButton} ${s.nextButton}`}
                                onClick={scrollNext}
                                disabled={activeDot === promotions.length - 1}
                                aria-label="Next banner"
                            >
                                &#8250;
                            </button>
                        </>
                    )}
                </div>

                {promotions.length > 1 && (
                    <div className={s.indicators}>
                        {Array.from({
                            length: promotions.length
                        }).map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={`${s.indicator} ${
                                    activeDot === index
                                        ? s.activeIndicator
                                        : ""
                                }`}
                                onClick={() => scrollToDot(index)}
                                aria-label={`Go to banner ${index + 1}`}
                                aria-current={
                                    activeDot === index
                                        ? "true"
                                        : undefined
                                }
                            />
                        ))}
                    </div>
                )}

            </div>

        </section>

    );

};

export default BannerPromotion;
